/**
 * Committed Initiatives — title, type, target window, status. "Blocked" is a
 * real status and requires a written blocker description (enforced in the form
 * and by a DB CHECK constraint).
 */

import { useState } from 'react';
import { Plus, Pencil, Target } from 'lucide-react';
import type {
  AssembledPor,
  PorCommitment,
  PorCommitmentStatus,
  PorCommitmentType,
} from '../../types/plan-of-record';
import { createCommitment, updateCommitment, deleteCommitment } from '../../lib/planOfRecordService';
import {
  CommitmentStatusPill,
  GhostButton,
  PorCard,
  PorCardHeader,
  PorEmptyState,
  PorModal,
  PrimaryButton,
} from './porUi';
import { PorNarrativeMarkdown } from './PorNarrativeMarkdown';

const STATUSES: PorCommitmentStatus[] = ['active', 'next', 'blocked', 'complete', 'watch'];
const TYPES: PorCommitmentType[] = ['hard', 'soft', 'aspirational'];

export function CommitmentsPanel({
  assembled,
  refresh,
}: {
  assembled: AssembledPor;
  refresh: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<PorCommitment | 'new' | null>(null);
  const commitments = assembled.commitments;

  return (
    <PorCard>
      <PorCardHeader
        title="Committed Initiatives"
        count={commitments.length}
        action={
          <GhostButton onClick={() => setEditing('new')}>
            <span className="inline-flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Add
            </span>
          </GhostButton>
        }
      />

      {commitments.length === 0 ? (
        <PorEmptyState
          icon={<Target className="h-6 w-6" />}
          title="No committed initiatives yet"
          prompt="A Plan of Record names what the organization has committed to — with an owner, a target window, and an honest status."
          action={<GhostButton onClick={() => setEditing('new')}>Add the first commitment</GhostButton>}
        />
      ) : (
        <div className="divide-y divide-[var(--rule)]">
          {commitments.map((c) => (
            <div key={c.id} className="py-3 flex items-start gap-3 group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[var(--text-pri)]">{c.title}</span>
                  <CommitmentStatusPill status={c.status} />
                  <span className="text-[11px] font-mono uppercase text-[var(--text-ter)]">{c.type}</span>
                </div>
                {c.description && (
                  <div className="mt-0.5 text-xs">
                    <PorNarrativeMarkdown source={c.description} />
                  </div>
                )}
                {c.status === 'blocked' && c.blocker_description && (
                  <div className="mt-1 text-xs text-luminous-coral-800 dark:text-luminous-coral-300">
                    <span className="font-semibold block mb-0.5">Blocked</span>
                    <PorNarrativeMarkdown source={c.blocker_description} />
                  </div>
                )}
                <div className="flex items-center gap-3 mt-1 text-[11px] text-[var(--text-ter)]">
                  {c.target_window_label && <span>Window · {c.target_window_label}</span>}
                  {c.owner_ids.length > 0 && <span>{c.owner_ids.length} owner(s)</span>}
                </div>
              </div>
              <button
                onClick={() => setEditing(c)}
                className="text-[var(--text-ter)] hover:text-[var(--text-sec)] opacity-0 group-hover:opacity-100 transition-opacity mt-0.5"
                aria-label="Edit commitment"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CommitmentModal
          porId={assembled.id}
          commitment={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </PorCard>
  );
}

function CommitmentModal({
  porId,
  commitment,
  onClose,
  onDone,
}: {
  porId: string;
  commitment: PorCommitment | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [title, setTitle] = useState(commitment?.title ?? '');
  const [description, setDescription] = useState(commitment?.description ?? '');
  const [type, setType] = useState<PorCommitmentType>(commitment?.type ?? 'soft');
  const [status, setStatus] = useState<PorCommitmentStatus>(commitment?.status ?? 'next');
  const [windowLabel, setWindowLabel] = useState(commitment?.target_window_label ?? '');
  const [blocker, setBlocker] = useState(commitment?.blocker_description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blockedNeedsReason = status === 'blocked' && blocker.trim().length === 0;
  const valid = title.trim().length > 0 && !blockedNeedsReason;

  const handleSave = async () => {
    if (!valid) {
      setError(
        title.trim().length === 0
          ? 'Title is required.'
          : 'A blocked commitment needs a written blocker description.',
      );
      return;
    }
    setBusy(true);
    setError(null);
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      type,
      status,
      target_window_label: windowLabel.trim() || null,
      blocker_description: status === 'blocked' ? blocker.trim() : null,
    };
    const { error: err } = commitment
      ? await updateCommitment(commitment.id, payload)
      : await createCommitment(porId, payload);
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const handleDelete = async () => {
    if (!commitment) return;
    setBusy(true);
    const { error: err } = await deleteCommitment(commitment.id);
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const field = 'w-full text-sm  border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none';
  const label = 'block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5';

  return (
    <PorModal
      title={commitment ? 'Edit commitment' : 'Add commitment'}
      onClose={onClose}
      footer={
        <>
          {commitment && (
            <button
              onClick={handleDelete}
              disabled={busy}
              className="mr-auto text-sm font-medium text-luminous-coral-700 hover:text-luminous-coral-800 disabled:opacity-40"
            >
              Delete
            </button>
          )}
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={label}>Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={field} autoFocus />
        </div>
        <div>
          <label className={label}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className={`${field} resize-none`}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={label}>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value as PorCommitmentType)} className={field}>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as PorCommitmentStatus)}
              className={field}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Window</label>
            <input
              value={windowLabel}
              onChange={(e) => setWindowLabel(e.target.value)}
              placeholder='e.g. "90d"'
              className={field}
            />
          </div>
        </div>

        {status === 'blocked' && (
          <div>
            <label className={label}>
              Blocker description <span className="text-luminous-coral-700">*</span>
            </label>
            <textarea
              value={blocker}
              onChange={(e) => {
                setBlocker(e.target.value);
                if (error) setError(null);
              }}
              rows={2}
              placeholder="What, specifically, is blocking this? Ambiguity in status is what this product exists to eliminate."
              className={`${field} resize-none`}
            />
          </div>
        )}

        {error && <p className="text-xs text-luminous-coral-700">{error}</p>}
      </div>
    </PorModal>
  );
}
